import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Search, ArrowUp, MoreHorizontal, FilePlus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { LopSearchableSelect } from '../LopSearchableSelect';
import { LopSearchModal } from '../LopSearchModal';
import { searchRecords } from '../../api/search';
import { t } from '../../i18n/t';
import { LopButton } from '../LopButton';
import { showErrorToast } from '../ErrorToast';
import { useTabDirty } from '../../tabs';
import './LopRecordForm.css';
import { LopDateInput } from '../../components/LopDateInput';

export type LopFieldType = 'text' | 'number' | 'date' | 'bit' | 'fk';

export interface LopFieldDef {
  field: string;
  label: string;
  type: LopFieldType;
  required?: boolean;
  editableOnAddOnly?: boolean; // PK: editable only in "new" mode
  fkOptions?: { id: number; name: string }[];
  fkAllowNone?: boolean;
  // Optional help text shown as a hover tooltip on the field label (e.g. to
  // explain a manually-entered value that can't be derived/computed).
  tooltip?: string;
  // Optional grouping: fields sharing a section render together under a titled
  // fieldset. Fields with no section render in one ungrouped grid (the previous
  // behavior — used by simpler forms like FundRecordPage).
  section?: string;
  // Optional dynamic visibility: return true to HIDE this field for the current
  // record. Re-evaluated on every value change (e.g. AssetRecordPage hides
  // option-only fields for non-option asset types). Undefined → always shown.
  hidden?: (record: Record<string, any>) => boolean;
}

export interface LopHeaderChip {
  key: string;
  label: string;
  value: string;
  icon?: LucideIcon;
  // Shown as plain text in the compact (scrolled) header line.
  inline?: boolean;
}

export interface LopHeaderIdentity {
  name: string | null;
  chips: LopHeaderChip[];
}

export interface LopRecordFormProps<T extends Record<string, any>> {
  title: string;
  fieldDefs: LopFieldDef[];
  idField: string; // PK field name, e.g. 'assetId'
  searchTable: string; // for the top autocomplete + modal
  searchColumns: { field: string; headerName: string }[]; // modal grid columns
  suggestionLabel: (row: Record<string, unknown>) => string; // e.g. `${row.assetId} - ${row.assetName}`
  loadRecord: (id: number) => Promise<T>;
  // Returns the PERSISTED record (with any server-assigned id) so the form can
  // adopt it — essential for IDENTITY PKs (e.g. funds), where the new id is only
  // known after the INSERT.
  saveRecord: (record: T, isNew: boolean) => Promise<T>;
  newRecord: () => T; // blank record (defaults)
  initialId?: number | null; // deep-link: load this record on mount
  searchPlaceholder?: string; // top search box placeholder (e.g. "חיפוש נייר")
  // Optional: fields shown (bold) in the sticky header so the current record
  // stays identified while scrolling a long form. In "new" mode the generic
  // "new record" label shows instead.
  summaryFields?: { field: string; label?: string }[];
  // Optional caller-rendered extra UI shown below the form body (e.g.
  // FundRecordPage renders FundAccountsPanel here in edit mode only). The
  // prop is opaque to LopRecordForm — the caller decides what to show and
  // when. Render is unwrapped; the panel owns its own margins/padding.
  renderExtraPanel?: (ctx: { record: T; mode: 'edit' | 'new' }) => React.ReactNode;
  // Internal tabs (plan §3.6): fired whenever the loaded record or mode
  // changes (on selectRecord, "new", and immediately after a successful
  // save) so the page can derive its own tab title via useTabTitle — e.g.
  // FundRecordPage sets "הגדרת קרן · קסם 20" once a real fund is loaded, or
  // falls back to its own default title in "new"/unloaded mode. Optional;
  // LopRecordForm itself has no opinion on tab titles.
  onRecordChange?: (record: T, mode: 'edit' | 'new') => void;
  // Optional badge rendered in the sticky header next to the record identity
  // (e.g. the asset form's "נייר כללי" / "נייר של הטננט" source label).
  headerBadge?: (record: T, mode: 'edit' | 'new') => React.ReactNode;
  // Optional identity card content for the sticky header: the record NAME
  // (title) and quiet chips (id / type / ...). Chips with an empty value are
  // omitted. Without it the header falls back to summaryFields / the id.
  headerIdentity?: (record: T, isNew: boolean) => LopHeaderIdentity;
  // Optional: when true for the loaded record, every field and the Save button
  // are disabled and `readOnlyNote` is shown under the header. "New" stays available.
  isRecordReadOnly?: (record: T, mode: 'edit' | 'new') => boolean;
  readOnlyNote?: string;
}

type Mode = 'edit' | 'new';
type Status = { kind: 'success' | 'error'; text: string } | null;

const DEBOUNCE_MS = 250;

// yyyy-MM-dd for <input type="date">. The API returns ISO datetime for DATE
// columns — take the first 10 chars.
function toDateInputValue(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) return '';
  return value.slice(0, 10);
}

// Full human-readable value for the hover tooltip (so a value truncated in a
// narrow box stays readable). Empty string → no tooltip.
function displayValue(def: LopFieldDef, value: unknown): string {
  if (value == null || value === '') return '';
  switch (def.type) {
    case 'bit':
      return value ? t('common.yes') : t('common.no');
    case 'date':
      return toDateInputValue(value);
    case 'fk': {
      const opt = (def.fkOptions ?? []).find((o) => o.id === Number(value));
      return opt ? `${opt.id} - ${opt.name}` : String(value);
    }
    default:
      return String(value);
  }
}

// Generic single-record form: search → load one record → edit, or "new" →
// insert. Reusable infra; domain wiring lives at the page.
/**
 * Thrown by a page's saveRecord when the USER aborted the save (e.g. "ביטול" in a confirm dialog). Not an
 * operation failure: shown as a quiet inline status with the given message, never as an error toast.
 */
export class SaveCancelledError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SaveCancelledError';
  }
}

export function LopRecordForm<T extends Record<string, any>>({
  title,
  fieldDefs,
  idField,
  searchTable,
  searchColumns,
  suggestionLabel,
  loadRecord,
  saveRecord,
  newRecord,
  initialId,
  searchPlaceholder,
  renderExtraPanel,
  summaryFields,
  onRecordChange,
  headerBadge,
  headerIdentity,
  isRecordReadOnly,
  readOnlyNote,
}: LopRecordFormProps<T>) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const scrollToTop = useCallback(() => {
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const [record, setRecord] = useState<T>(() => newRecord());
  const [mode, setMode] = useState<Mode>('new');
  const [dirty, setDirty] = useState(false);
  const [compact, setCompact] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuBtnRef = useRef<HTMLButtonElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<Status>(null);

  // Internal tabs (plan §3.6): report this form's unsaved-changes state to
  // its own tab, and let the page react to the loaded record/mode (title).
  useTabDirty(dirty);
  useEffect(() => {
    onRecordChange?.(record, mode);
    // onRecordChange intentionally omitted: pages typically pass an inline
    // callback, and re-running this on every render of THEIRS (not just when
    // record/mode change here) would be wasted work — the callback itself is
    // idempotent for an unchanged record/mode (it just calls setState with
    // the same computed string).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, mode]);

  // Top autocomplete search state.
  const [term, setTerm] = useState('');
  const [suggestions, setSuggestions] = useState<Record<string, unknown>[]>([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement | null>(null);

  // selectRecord guards against losing unsaved edits, then loads the record.
  const selectRecord = useCallback(
    async (id: number) => {
      if (dirty && !window.confirm(t('recordForm.unsavedConfirm'))) return;
      setLoading(true);
      setStatus(null);
      try {
        const loaded = await loadRecord(id);
        setRecord(loaded);
        setMode('edit');
        setDirty(false);
      } catch (err) {
        showErrorToast(err, { title: t('errorToast.titleLoad') });
        setStatus({ kind: 'error', text: t('recordForm.loadError') });
      } finally {
        setLoading(false);
      }
    },
    [dirty, loadRecord],
  );

  // Deep-link: load initialId once on mount.
  const initialLoadedRef = useRef(false);
  useEffect(() => {
    if (initialLoadedRef.current) return;
    if (initialId != null && Number.isFinite(initialId)) {
      initialLoadedRef.current = true;
      void selectRecord(initialId);
    }
    // selectRecord intentionally omitted: mount-only deep-link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialId]);

  // Debounced autocomplete suggestions.
  useEffect(() => {
    if (!suggestOpen) return;
    let cancelled = false;
    const handle = window.setTimeout(() => {
      searchRecords(searchTable, term)
        .then((data) => {
          if (cancelled) return;
          setSuggestions(data);
        })
        .catch(() => {
          if (cancelled) return;
          setSuggestions([]);
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [searchTable, term, suggestOpen]);

  // Close suggestions on outside click.
  useEffect(() => {
    if (!suggestOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (
        searchBoxRef.current &&
        !searchBoxRef.current.contains(e.target as Node)
      ) {
        setSuggestOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [suggestOpen]);

  // Compact header: a sentinel sits just above the sticky header; once it
  // scrolls out of the scrolling ancestor (the shell content area, which is the
  // same element for every keep-alive pane) the header compacts. No scroll
  // listeners. A hidden (inert) pane's observer is harmless — it re-evaluates
  // when the pane is shown again.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let root: Element | null = el.parentElement;
    while (root) {
      const oy = getComputedStyle(root).overflowY;
      if (oy === 'auto' || oy === 'scroll') break;
      root = root.parentElement;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        const rootTop = entry.rootBounds?.top ?? 0;
        setCompact(!entry.isIntersecting && entry.boundingClientRect.top < rootTop);
      },
      { root, threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Overflow menu: close on outside click / Escape (focus returns to trigger).
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        menuBtnRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const setField = useCallback((field: string, value: unknown) => {
    setRecord((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    setStatus(null);
  }, []);

  const handleNew = useCallback(() => {
    if (dirty && !window.confirm(t('recordForm.unsavedConfirm'))) return;
    setRecord(newRecord());
    setMode('new');
    setDirty(false);
    setStatus(null);
  }, [dirty, newRecord]);

  const handleSelectSuggestion = useCallback(
    (row: Record<string, unknown>) => {
      setSuggestOpen(false);
      setTerm('');
      const id = Number(row[idField]);
      if (Number.isFinite(id)) void selectRecord(id);
    },
    [idField, selectRecord],
  );

  const handleModalSelect = useCallback(
    (row: Record<string, unknown>) => {
      const id = Number(row[idField]);
      if (Number.isFinite(id)) void selectRecord(id);
    },
    [idField, selectRecord],
  );

  // In "new" mode, if the user types a PK that already exists, offer to load the
  // existing record on blur (natural-PK screens only — an IDENTITY PK is never a
  // user-entered field, so this never fires there).
  const handlePkBlur = useCallback(async () => {
    if (mode !== 'new') return;
    const idVal = record[idField];
    const idNum = Number(idVal);
    if (idVal == null || idVal === '' || !Number.isFinite(idNum) || idNum <= 0) {
      return;
    }
    try {
      const existing = await loadRecord(idNum);
      if (existing && window.confirm(t('recordForm.existsConfirm'))) {
        setRecord(existing);
        setMode('edit');
        setDirty(false);
        setStatus(null);
      }
    } catch {
      // Not found (404) → it's genuinely a new record; proceed.
    }
  }, [mode, record, idField, loadRecord]);

  const validate = useCallback((): boolean => {
    for (const def of fieldDefs) {
      if (!def.required) continue;
      const value = record[def.field];
      if (def.type === 'fk') {
        if (value == null) return false;
      } else if (def.type === 'number') {
        if (value == null || value === '' || Number.isNaN(Number(value))) {
          return false;
        }
      } else if (def.type === 'bit') {
        // boolean is always "present".
        continue;
      } else {
        if (value == null || String(value).trim() === '') return false;
      }
    }
    return true;
  }, [fieldDefs, record]);

  const handleSave = useCallback(async () => {
    if (!validate()) {
      setStatus({ kind: 'error', text: t('recordForm.requiredError') });
      return;
    }
    setSaving(true);
    setStatus(null);
    try {
      const saved = await saveRecord(record, mode === 'new');
      setRecord(saved); // adopt the persisted record (server-assigned id for IDENTITY PKs)
      setMode('edit');
      setDirty(false);
      setStatus({ kind: 'success', text: t('recordForm.saved') });
    } catch (err) {
      if (err instanceof SaveCancelledError) {
        setStatus({ kind: 'error', text: err.message });
      } else {
        showErrorToast(err, { title: t('errorToast.titleSave') });
      }
    } finally {
      setSaving(false);
    }
  }, [validate, saveRecord, record, mode]);

  const identity = useMemo(() => {
    if (mode === 'new') return t('recordForm.newRecord');
    const idValue = record[idField];
    return idValue == null ? t('recordForm.newRecord') : String(idValue);
  }, [mode, record, idField]);

  const headerId = useMemo<LopHeaderIdentity | null>(() => {
    if (!headerIdentity) return null;
    const h = headerIdentity(record, mode === 'new');
    return { name: h.name, chips: h.chips.filter((c) => c.value !== '') };
  }, [headerIdentity, record, mode]);
  const headerName = headerId
    ? (headerId.name && headerId.name !== '' ? headerId.name : t('recordForm.newRecord'))
    : identity;

  // Group fields by section, preserving first-appearance order. Fields with no
  // section collapse into a single leading group (key '') rendered without a
  // titled fieldset — keeps simpler forms (FundRecordPage) visually unchanged.
  const groups = useMemo(() => {
    const order: string[] = [];
    const bySection = new Map<string, LopFieldDef[]>();
    for (const def of fieldDefs) {
      if (def.hidden?.(record)) continue; // dynamic per-record visibility
      const key = def.section ?? '';
      if (!bySection.has(key)) {
        bySection.set(key, []);
        order.push(key);
      }
      bySection.get(key)!.push(def);
    }
    return order.map((key) => ({ section: key, fields: bySection.get(key)! }));
  }, [fieldDefs, record]);

  const recordLocked = isRecordReadOnly?.(record, mode) === true;

  const renderControl = (def: LopFieldDef) => {
    const controlId = `lrf-${def.field}`;
    const isReadOnly = recordLocked || (def.editableOnAddOnly === true && mode !== 'new');
    const isPk = def.field === idField && def.editableOnAddOnly === true;
    const rawValue = record[def.field];

    switch (def.type) {
      case 'number':
        return (
          <input
            id={controlId}
            type="number"
            className="lop-rf__input"
            value={rawValue == null ? '' : String(rawValue)}
            disabled={isReadOnly}
            onBlur={isPk ? () => void handlePkBlur() : undefined}
            onChange={(e) =>
              setField(
                def.field,
                e.target.value === '' ? null : Number(e.target.value),
              )
            }
          />
        );
      case 'date':
        return (
          <LopDateInput
            id={controlId}
            className="lop-rf__input"
            wrapperClassName="lop-rf__date"
            value={toDateInputValue(rawValue)}
            disabled={isReadOnly}
            onChange={(v) => setField(def.field, v === '' ? null : v)}
          />
        );
      case 'bit':
        return (
          <label className="lop-rf__toggle">
            <input
              id={controlId}
              type="checkbox"
              className="lop-rf__toggle-input"
              checked={Boolean(rawValue)}
              disabled={isReadOnly}
              onChange={(e) => setField(def.field, e.target.checked)}
            />
            <span className="lop-rf__toggle-track" aria-hidden="true">
              <span className="lop-rf__toggle-thumb" />
            </span>
          </label>
        );
      case 'fk':
        return (
          <LopSearchableSelect
            id={controlId}
            value={rawValue == null ? null : Number(rawValue)}
            options={def.fkOptions ?? []}
            allowNone={def.fkAllowNone}
            disabled={isReadOnly}
            onChange={(id) => setField(def.field, id)}
          />
        );
      case 'text':
      default:
        return (
          <input
            id={controlId}
            type="text"
            className="lop-rf__input"
            value={rawValue == null ? '' : String(rawValue)}
            disabled={isReadOnly}
            onBlur={isPk ? () => void handlePkBlur() : undefined}
            onChange={(e) =>
              setField(def.field, e.target.value === '' ? null : e.target.value)
            }
          />
        );
    }
  };

  return (
    <div className="lop-rf" ref={rootRef}>
      {/* Top search bar: autocomplete + magnifier modal. */}
      <div className="lop-rf__searchbar">
        <div className="lop-rf__search" ref={searchBoxRef}>
          <input
            type="text"
            className="lop-rf__search-input"
            value={term}
            placeholder={searchPlaceholder ?? t('recordForm.searchPlaceholder')}
            autoComplete="off"
            onFocus={() => setSuggestOpen(true)}
            onChange={(e) => {
              setTerm(e.target.value);
              setSuggestOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setSuggestOpen(false);
            }}
          />
          {suggestOpen && suggestions.length > 0 && (
            <ul className="lop-rf__suggestions" role="listbox">
              {suggestions.map((row, i) => (
                <li
                  key={String(row[idField] ?? i)}
                  className="lop-rf__suggestion"
                  role="option"
                  aria-selected={false}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectSuggestion(row);
                  }}
                >
                  {suggestionLabel(row)}
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          className="lop-rf__icon-btn"
          aria-label={t('recordForm.searchTitle')}
          onClick={() => setModalOpen(true)}
        >
          <Search size={18} />
        </button>
      </div>

      {/* Sticky identity header: expanded card at the top, one compact line
          once scrolled (sentinel + IntersectionObserver). */}
      <div ref={sentinelRef} className="lop-rf__sentinel" aria-hidden="true" />
      <header className={`lop-rf__header${compact ? ' lop-rf__header--compact' : ''}`} data-compact={compact}>
        <div className="lop-rf__identity">
          {!compact && <span className="lop-rf__eyebrow">{title}</span>}
          <div className="lop-rf__titlerow">
            <h1 className="lop-rf__name" title={headerName}>{headerName}</h1>
            {headerBadge?.(record, mode)}
            {compact && headerId && headerId.chips.some((c) => c.inline) && (
              <span className="lop-rf__ids">
                {headerId.chips
                  .filter((c) => c.inline)
                  .map((c) => c.value)
                  .join(' · ')}
              </span>
            )}
            {!headerId && mode !== 'new' && summaryFields && summaryFields.length > 0 && (
              <div className="lop-rf__summary">
                {summaryFields.map((sf) => {
                  const v = record[sf.field];
                  const text = v == null || v === '' ? '—' : String(v);
                  return (
                    <span className="lop-rf__summary-item" key={sf.field}>
                      {sf.label && <span className="lop-rf__summary-label">{sf.label}</span>}
                      <strong className="lop-rf__summary-value" title={text}>{text}</strong>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          {!compact && headerId && headerId.chips.length > 0 && (
            <ul className="lop-rf__chips">
              {headerId.chips.map((c) => {
                const Icon = c.icon;
                return (
                  <li className="lop-rf__chip" key={c.key} data-chip-key={c.key} title={`${c.label}: ${c.value}`}>
                    {Icon && <Icon size={13} aria-hidden="true" />}
                    <span className="lop-rf__chip-label">{c.label}</span>
                    <span className="lop-rf__chip-value">{c.value}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="lop-rf__actions">
          {dirty && (
            <span className="lop-rf__dirty" role="status" data-testid="record-dirty">
              <span className="lop-rf__dirty-dot" aria-hidden="true" />
              {t('recordForm.unsavedChanges')}
            </span>
          )}
          <LopButton
            variant="primary"
            disabled={recordLocked}
            loading={saving}
            onClick={() => handleSave()}
          >
            {t('recordForm.save')}
          </LopButton>
          <div className="lop-rf__menu" ref={menuRef}>
            <button
              type="button"
              ref={menuBtnRef}
              className="lop-rf__icon-btn"
              aria-label={t('recordForm.moreActions')}
              title={t('recordForm.moreActions')}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-controls="lrf-actions-menu"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <MoreHorizontal size={18} />
            </button>
            {menuOpen && (
              <div className="lop-rf__menu-list" id="lrf-actions-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="lop-rf__menu-item"
                  autoFocus
                  onClick={() => {
                    setMenuOpen(false);
                    handleNew();
                  }}
                >
                  <FilePlus size={15} aria-hidden="true" />
                  {t('recordForm.new')}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="lop-rf__menu-item"
                  onClick={() => {
                    setMenuOpen(false);
                    scrollToTop();
                  }}
                >
                  <ArrowUp size={15} aria-hidden="true" />
                  {t('recordForm.backToTop')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {recordLocked && readOnlyNote && (
        <div className="lop-rf__status lop-rf__status--success" role="note" data-testid="record-readonly-note">
          {readOnlyNote}
        </div>
      )}

      {status && (
        <div
          className={`lop-rf__status lop-rf__status--${status.kind}`}
          role={status.kind === 'error' ? 'alert' : 'status'}
        >
          {status.text}
        </div>
      )}

      {/* Form body. */}
      <div className="lop-rf__body">
        {groups.map((group) => {
          const grid = (
            <div className="lop-rf__grid">
              {group.fields.map((def) => {
                const controlId = `lrf-${def.field}`;
                const full = displayValue(def, record[def.field]);
                // text/number/date use the overlay-expand control wrapper;
                // fk + bit don't truncate the same way, so they render plain.
                const overlay =
                  def.type === 'text' ||
                  def.type === 'number' ||
                  def.type === 'date';
                return (
                  <div
                    className="lop-rf__field"
                    key={def.field}
                    title={full || undefined}
                  >
                    <label
                      className="lop-rf__label"
                      htmlFor={controlId}
                      title={def.tooltip}
                    >
                      {def.label}
                      {def.required && (
                        <span className="lop-rf__required" aria-hidden="true">
                          *
                        </span>
                      )}
                    </label>
                    {overlay ? (
                      <div className="lop-rf__control">{renderControl(def)}</div>
                    ) : (
                      renderControl(def)
                    )}
                  </div>
                );
              })}
            </div>
          );
          return group.section === '' ? (
            <React.Fragment key="__nosection">{grid}</React.Fragment>
          ) : (
            <fieldset className="lop-rf__section" key={group.section}>
              <legend className="lop-rf__legend">{group.section}</legend>
              {grid}
            </fieldset>
          );
        })}

        {loading && (
          <div className="lop-rf__overlay">{t('recordForm.loading')}</div>
        )}
      </div>

      {renderExtraPanel?.({ record, mode })}

      {modalOpen && (
        <LopSearchModal
          title={t('recordForm.searchTitle')}
          table={searchTable}
          columns={searchColumns}
          onSelect={handleModalSelect}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}
